/**
 * Общий Stepper для ипотечного флоу.
 * 5 шагов: Параметры → Расчёт → Оформление → Оплата → Документы.
 *
 * Навигация — ТОЛЬКО назад по уже пройденным шагам, и «Параметры» (самое
 * начало) ссылкой не делаем никогда: с готовой заявкой возвращаться на чистый
 * /ipoteka нельзя (потеря заявки — параметры правятся в форме на /offers).
 * Будущие шаги некликабельны.
 *
 * «Документы» (/ipoteka/docs) — после оплаты, но ПОКА НЕ АКТИВЕН: показываем
 * шаг, но кликать нельзя (флоу документов ещё не готов). Включить — убрать
 * 'docs' из guard ниже.
 *
 * Итог по экранам:
 *   welcome  (active=0) — ссылок нет (расчёт ещё не начат);
 *   offers   (active=1) — ссылок нет (позади только «Параметры»);
 *   checkout (active=2) — ссылка только назад на «Расчёт стоимости»;
 *   payment  (active=3) — ссылки на «Оформление полиса» и «Расчёт стоимости».
 *
 * `hrefByKey` переопределяет ссылку конкретного шага по ключу. Так payment
 * отдаёт checkout-ссылку с partner/company/offer, чтобы вернуться к нужной СК.
 *
 * Если `ipotekaId` пришёл в пропсах — используется он. Иначе берём последний
 * сохранённый из localStorage (test-mode для прощёлкивания экранов на welcome).
 */

import { getLastIpotekaId } from './ipotekaTestId';

export interface StepperProps {
    active: 0 | 1 | 2 | 3 | 4;
    ipotekaId?: string;
    /** Переопределение href отдельных шагов по ключу (welcome/offers/checkout/payment/docs). */
    hrefByKey?: Partial<Record<string, string>>;
}

interface Step {
    key: string;
    label: string;
    href: (id: string) => string | null;
}

/** Шаги, которые НИКОГДА не делаем ссылкой: welcome (потеря заявки) и docs (флоу ещё не готов). */
const NON_CLICKABLE_KEYS = new Set(['welcome', 'docs']);

const STEPS: Step[] = [
    { key: 'welcome', label: 'Параметры', href: () => '/ipoteka' },
    { key: 'offers', label: 'Расчёт стоимости', href: (id) => (id ? `/ipoteka/offers?id=${encodeURIComponent(id)}` : null) },
    { key: 'checkout', label: 'Оформление полиса', href: (id) => (id ? `/ipoteka/checkout?id=${encodeURIComponent(id)}` : null) },
    { key: 'payment', label: 'Оплата', href: (id) => (id ? `/ipoteka/payment?id=${encodeURIComponent(id)}` : null) },
    { key: 'docs', label: 'Документы', href: (id) => (id ? `/ipoteka/docs?id=${encodeURIComponent(id)}` : null) },
];

export function Stepper({ active, ipotekaId = '', hrefByKey }: StepperProps) {
    // Если id не пришёл из URL — пробуем взять последний сохранённый
    // (нужно чтобы со страницы welcome можно было прощёлкивать другие шаги).
    const effectiveId = ipotekaId || getLastIpotekaId();

    return (
        <div className="ab-stepper">
            {STEPS.map((s, i) => {
                const isActive = i === active;
                const isDone = i < active;
                const href = hrefByKey?.[s.key] ?? s.href(effectiveId);
                // Кликабельны только пройденные шаги (навигация назад), кроме
                // принципиально некликабельных (welcome/docs). Активный и будущие
                // шаги — статичны.
                const isClickable = isDone && !NON_CLICKABLE_KEYS.has(s.key) && !!href;
                const cls = [
                    'ab-stepper-step',
                    isDone ? 'is-done' : '',
                    isActive ? 'is-active' : '',
                    isClickable ? 'is-clickable' : '',
                    !isActive && !isClickable ? 'is-disabled' : '',
                ]
                    .filter(Boolean)
                    .join(' ');

                const inner = (
                    <>
                        <span className="ab-stepper-num">{isDone ? '✓' : i + 1}</span>
                        <span>{s.label}</span>
                    </>
                );

                return isClickable ? (
                    <a key={s.key} href={href!} className={cls}>
                        {inner}
                    </a>
                ) : (
                    <span key={s.key} className={cls}>
                        {inner}
                    </span>
                );
            })}
        </div>
    );
}
